import { useCallback, useEffect, useState } from "react";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import { useBoolean } from "src/hooks/use-boolean";
import useTable from "src/hooks/use-table/use-table";
import useResponsive from "src/hooks/useResponsive";
import { INotificationsListDto, IUnreadNotificationsCountDto } from "src/types/notifications";
import { NOTIFICATIONS_TABS } from "./notifications-tabs";
import { useQueryClient } from "@tanstack/react-query";

const useNotificationsDrawer = () => {
  // #region States and hooks
  const queryClient = useQueryClient();
  const drawer = useBoolean();
  const pagination = useTable();
  const smUp = useResponsive("up", "sm");

  const isUnReadOnly = useBoolean();
  const renderPagination = useBoolean();

  const { useGetListApi, usePatchApi } = useApiServices();
  const [currentTab, setCurrentTab] = useState("all");
  // #endregion States and hooks

  // #region Services
  const { data: notifications, isLoading: isLoadingNotifications } = useGetListApi<
    INotificationsListDto,
    { page: number; per_page: number; is_read?: boolean }
  >({
    url: API_URLS.NOTIFICATIONS.root,
    parameters: {
      per_page: pagination.rowsPerPage,
      page: pagination.page,
      is_read: isUnReadOnly.value ? false : undefined,
    },
  });

  const { data: unreadNotificationsCount, fetchStatus: countFetchStatus } =
    useGetListApi<IUnreadNotificationsCountDto>({
      url: API_URLS.NOTIFICATIONS.unreadCount,
    });

  const { mutate: markAllAsRead, isLoading: isMarkingAll } = usePatchApi({
    url: API_URLS.NOTIFICATIONS.readAll,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_URLS.NOTIFICATIONS.root] });
      queryClient.invalidateQueries({ queryKey: [API_URLS.NOTIFICATIONS.unreadCount] });
    },
  });
  // #endregion Services

  // #region Handlers
  const handleMarkAllAsRead = () => {
    markAllAsRead({});
  };

  const handleChangeTab = useCallback(
    (_: any, newValue: ReturnType<typeof NOTIFICATIONS_TABS>[number]["value"]) => {
      setCurrentTab(newValue);
      pagination.setPage(1);
      isUnReadOnly.setValue(newValue === "unread");
    },
    []
  );
  // #endregion Handlers

  useEffect(() => {
    const total = isUnReadOnly.value
      ? unreadNotificationsCount?.unread_count
      : notifications?.metadata.total_notifications;

    renderPagination.setValue(!!total && +total > +pagination.rowsPerPage);
  }, [isUnReadOnly.value, notifications, pagination.rowsPerPage, unreadNotificationsCount]);

  return {
    unreadNotificationsCount,
    handleMarkAllAsRead,
    smUp,
    drawer,
    currentTab,
    handleChangeTab,
    notifications,
    pagination,
    isLoadingNotifications,
    isUnReadOnly,
    renderPagination,
    isMarkingAll,
  };
};

export default useNotificationsDrawer;
